<template>
  <div class="vacation-request-block">
    <h3 v-if="embedded" class="card-title">Vacation Request</h3>
    <div v-if="!hideOnRequested" class="card" :class="{ 'mt-2': margin }">
      <div v-if="!embedded" class="card-header">
        <b-row>
          <b-col cols="6">
            <h3 v-if="isUpdateForm">{{ $dt('vacations.update_title', 'Update vacation request') }}</h3>
            <h3 v-else>{{ $dt('vacations.create_title', 'Create vacation request') }}</h3>
          </b-col>
        </b-row>
      </div>
      <div class="card-body">
        <form>
          <div v-if="user.isAdmin" class="form-group row">
            <label for="employee" class="col-sm-3 col-form-label">Employee</label>
            <div class="col-sm-9">
              <treeselect v-model="entity.jira_user_id" :class="{ 'error': errorMsg['jira_user_id'] }"
                :options="employeesOptions" placeholder="Select employee">
              </treeselect>
            </div>
          </div>
          <div class="form-group row">
            <label for="dates" class="col-sm-3 col-form-label">Dates</label>
            <div class="col">
              <Datepicker placeholder="From" :input-class="{ 'error': errorMsg['start'], 'date-picker': true }"
                v-model="entity.start" format="yyyy-MM-dd" />
            </div>
            <div class="col">
              <Datepicker placeholder="To" :input-class="{ 'error': errorMsg['end'], 'date-picker': true }"
                v-model="entity.end" format="yyyy-MM-dd" />
            </div>
          </div>
          <div class="form-group row">
            <label for="type" class="col-sm-3 col-form-label">Type</label>
            <div class="col-sm-9">
              <treeselect v-model="entity.vacation_type_id" :class="{ 'error': errorMsg['vacation_type_id'] }"
                :options="typesOptions" placeholder="Select vacation type" @input="onTypeChange">
              </treeselect>
              <div v-if="typeDescription" class="type-description">{{ typeDescription }}</div>
            </div>
          </div>
          <div v-if="isUpdateForm" class="form-group row">
            <label for="type" class="col-sm-3 col-form-label">Status</label>
            <div class="col-sm-9">
              <treeselect v-model="entity.status" :class="{ 'error': errorMsg['vacation_type_id'] }"
                :options="statusOptions" placeholder="Select vacation status">
              </treeselect>
            </div>
          </div>
          <div v-if="noteable" class="form-group row">
            <label class="col-md-3 form-control-label">Details</label>
            <div class="col-md-9">
              <textarea class="form-control" v-model="entity.details"></textarea>
            </div>
          </div>
          <div class="footer">
            <b-button type="submit" variant="primary" :disabled="spinners.isSaving" @click.prevent="save()">
              <b-spinner v-if="spinners.isSaving" type="grow" small />
              {{ isUpdateForm ? $dt('global.save', 'Save') : $dt('vacations.request_btn', 'Request') }}
            </b-button>
            <b-button v-if="!embedded" :to="prevRoute ? prevRoute : { name: 'VacationsList' }" variant="info"
              class="ml-2">
              {{ $dt('global.cancel-back', 'Back') }}
            </b-button>
          </div>
        </form>
      </div>
    </div>
    <div v-else class="card" :class="{ 'mt-2': margin }">
      <div class="card-body">
        <p>{{ $dt('vacations.requested', 'Your vacation request has been submitted.') }}</p>
        <div class="footer">
          <b-button variant="primary" :disabled="spinners.isSaving" @click.prevent="requested = false">
            {{ $dt('vacations.request_another_btn', 'Request another one') }}
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import Services from '@/services'
import Datepicker from 'vuejs-datepicker'
import Treeselect from '@riophae/vue-treeselect'

export default {
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from.name !== null && from.name !== 'VacationCreate') {
        const { name, params, query } = from
        vm.prevRoute = { name, params, query }
      }
    })
  },
  mounted() {
    this.init()
    this.load()
  },
  components: {
    Datepicker, Treeselect
  },
  props: {
    margin: {
      type: Boolean,
      default: false,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    // If id === null, form will be considered create form else update
    id: { type: Number, default: null },
    // pass errors from parent form if embedded
    errors: { type: Object, default() { return {} } },
    // value is used both as DEFAULTs placeholder and for v-model
    value: {
      type: Object,
      default() {
        return {
          vacation_type_id: null,
          jira_user_id: null,
          start: null,
          end: null,
          status: null,
          details: null
        }
      },
    },
  },
  data() {
    return {
      prevRoute: null,
      employees: [],
      types: [],
      statuses: [],
      entity: this.value,
      spinners: {
        contentIsLoading: false,
        isSaving: false,
      },
      submitting: false,
      errorMsg: this.errors,
      requested: false,
    }
  },
  computed: {
    ...mapState('Auth', ['user']),
    isUpdateForm() { return this.id !== null },
    employeesOptions() {
      const opt = this.employees.map((employee) => ({ id: employee.id, label: employee.displayName }))
      return opt
    },
    typesOptions() {
      const opt = this.types.map((type) => ({ id: type.id, label: type.name }))
      return opt
    },
    typeDescription() {
      const type = this.types.find(i => i.id === this.entity.vacation_type_id)
      return type?.description
    },
    statusOptions() {
      const opt = this.statuses.map((status) => ({ id: status, label: status }))
      return opt
    },
    noteable() {
      const type = this.types.find(i => i.id === this.entity.vacation_type_id)
      return type?.noteable
    },
    hideOnRequested() {
      if(this.embedded && this.requested) return true
      return false
    }
  },
  watch: {
  },
  methods: {
    getForUpdate: Services.vacations.editData,
    getForCreate: Services.vacations.createData,
    pushUpdate: Services.vacations.update,
    pushCreate: Services.vacations.create,
    init() {
      Services.vacations.formData().then(({ data }) => {
        this.employees = data.employees
        this.types = data.types
        this.statuses = data.statuses
        const currentUser = this.employees.find(i => i.email === this.user.email)
        if (this.embedded && currentUser?.id) this.entity.jira_user_id = currentUser.id
      })
    },
    load() {
      this.spinners.contentIsLoading = true
      const action = this.isUpdateForm ? this.getForUpdate : this.getForCreate
      action(this.id).then((data) => {
        this.entity = this.isUpdateForm ? data.data.entity : { ...this.entity, ...data.data.entity }
      }).catch((data) => {
        this.alertError({ title: 'Failed to get vacation type', text: '-' })
        this.errorMsg = data.data ? data.data.errors : {}
      }).finally(() => {
        this.spinners.contentIsLoading = false
      })
    },
    save() {
      if (this.spinners.isSaving) return
      const action = this.isUpdateForm && !this.embedded ? this.pushUpdate : this.pushCreate
      this.validate()
      if (Object.keys(this.errorMsg).length) return
      this.spinners.isSaving = true
      const reqData = {
        ...this.entity,
        start: moment(this.entity.start).format('YYYY-MM-DD'),
        end: moment(this.entity.end).format('YYYY-MM-DD'),
      }
      action(reqData).then((data) => {
        if (this.embedded) {
          this.entity = {
            ...this.entity,
            vacation_type_id: null,
            start: null,
            end: null,
            status: null,
            details: null
          }
        } else {
          if (!this.user.isAdmin) {
            this.$router.push({ name: 'VacationsList' })
          } else if (!this.isUpdateForm) {
            this.entity = {
              ...this.entity,
              ...data.data
            }
            this.$router.push({ name: 'VacationUpdate', params: { id: data.data.id } })
          }
        }
        this.alertSuccess({ title: 'Vacation requested!', text: '-' })
        this.errorMsg = {}
        if (this.embedded) this.requested = true
      }).catch((error) => {
        this.alertError({ title: 'Failed to save vacation', text: '-' })
        this.errorMsg = error.errors || {}
        console.log(error)
      }).finally(() => {
        this.spinners.isSaving = false
      })
    },
    validate() {
      this.errorMsg = {}
      if (!this.entity.vacation_type_id) {
        this.errorMsg['vacation_type_id'] = 'Type is required'
      }
      if (!this.entity.start) {
        this.errorMsg['start'] = 'From is required'
      } else {
        const start = moment(this.entity.start)
        if (!start.isValid()) {
          this.errorMsg['start'] = 'From date should be valid'
        }
        if (start.isAfter(this.entity.end)) {
          this.errorMsg['start'] = 'From date should be after Hire date'
        }
      }
      if (!this.entity.end) {
        this.errorMsg['end'] = 'To is required'
      } else {
        const end = moment(this.entity.end)
        if (!end.isValid()) {
          this.errorMsg['end'] = 'To date should be valid'
        }
      }
    },
    onTypeChange() {
      if (!this.spinners.contentIsLoading) this.entity.details = null
    }
  },
}

</script>

<style lang=scss>
.vacation-request-block {
  min-width: 265px;
}

.date-picker {
  width: 100%;
  border: 1px solid #e4e7ea;
  border-radius: 0.25rem;
  color: #5c6873;
  padding: 0.38rem 0.75rem;
}

.error {
  border: 1px solid #f86c6b;
  border-radius: 5px;
}

.type-description {
  white-space: pre-line;
  line-height: normal;
  font-size: 0.7rem;
  margin-left: 0.5rem;
  color: #6c757d;
}
</style>
